<template>
  <v-dialog v-model="dialog" max-width="640" persistent>
    <v-card>
      <v-card-title class="d-flex align-center ga-2">
        <v-icon size="20">mdi-send</v-icon>
        {{ $t("ncrm.integrations.telegram.send.title") }}
      </v-card-title>
      <v-divider />

      <v-card-text class="pt-4">
        <!-- No bot/no recipients -->
        <v-alert
          v-if="!loading && noBots"
          type="warning"
          density="compact"
          class="mb-3"
        >
          {{ $t("ncrm.integrations.telegram.send.noBots") }}
        </v-alert>
        <v-alert
          v-else-if="!loading && noRecipients"
          type="info"
          density="compact"
          class="mb-3"
        >
          {{ $t("ncrm.integrations.telegram.send.noRecipients") }}
        </v-alert>

        <!-- History panel (last 5 messages to this entity) -->
        <v-expansion-panels v-if="history.length" class="mb-4">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <v-icon start size="16">mdi-history</v-icon>
              {{ $t("ncrm.integrations.telegram.send.history.toggle", { n: history.length }) }}
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div
                v-for="m in history"
                :key="m.id"
                class="telegram-send-history-row d-flex flex-column ga-1 py-2"
              >
                <div class="d-flex align-center ga-2 flex-wrap">
                  <v-chip
                    size="x-small"
                    :color="m.status === 'sent' ? 'success' : 'error'"
                  >
                    {{ $t("ncrm.integrations.telegram.send.history.status." + m.status) }}
                  </v-chip>
                  <span class="text-caption text-medium-emphasis">
                    {{ formatDate(m.created_at) }}
                  </span>
                </div>
                <div class="telegram-send-history-text">{{ m.text }}</div>
                <div v-if="m.error_message" class="text-caption text-error">{{ m.error_message }}</div>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Compose form -->
        <template v-if="!noBots && !noRecipients">
          <v-select
            v-if="accounts.length > 1"
            v-model="form.accountId"
            :items="accountOptions"
            :label="$t('ncrm.integrations.telegram.send.fields.bot')"
            class="mb-2"
            @update:model-value="onAccountChange"
          />

          <v-select
            v-model="form.recipientKey"
            :items="recipientOptions"
            :label="$t('ncrm.integrations.telegram.send.fields.to')"
            :rules="[required]"
            required
            class="mb-2"
          />

          <v-select
            v-model="form.templateId"
            :items="templateOptions"
            :label="$t('ncrm.integrations.telegram.send.fields.template')"
            clearable
            class="mb-2"
            @update:model-value="onTemplateChange"
          />

          <!-- Variables panel for the selected template -->
          <div v-if="placeholders.length" class="mb-3">
            <div class="text-caption text-medium-emphasis mb-1">
              {{ $t("ncrm.integrations.telegram.send.fields.variables") }}
            </div>
            <v-text-field
              v-for="placeholder in placeholders"
              :key="placeholder"
              v-model="form.variables[placeholder]"
              :label="placeholder"
              density="compact"
              hide-details
              class="mb-2"
            />
          </div>

          <v-textarea
            v-model="form.text"
            :label="
              form.templateId
                ? $t('ncrm.integrations.telegram.send.fields.preview')
                : $t('ncrm.integrations.telegram.send.fields.freeText')
            "
            rows="4"
            auto-grow
            :readonly="!!form.templateId"
            :hint="
              form.templateId
                ? $t('ncrm.integrations.telegram.send.fields.previewHint')
                : ''
            "
            persistent-hint
          />
        </template>

        <v-alert v-if="errorMessage" type="error" density="compact" class="mt-3">
          {{ errorMessage }}
        </v-alert>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="sending" @click="close">
          {{ $t("ncrm.integrations.telegram.send.actions.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="sending"
          :disabled="!canSend"
          @click="submit"
        >
          <v-icon start>mdi-send</v-icon>
          {{ $t("ncrm.integrations.telegram.send.actions.send") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";

const PLACEHOLDER_RE = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g;

const extractPlaceholders = (body) => {
  const set = new Set();
  String(body || "").replace(PLACEHOLDER_RE, (_, key) => {
    set.add(key);
    return "";
  });
  return Array.from(set);
};

const renderBody = (body, variables) => {
  const safe = variables && typeof variables === "object" ? variables : {};
  return String(body || "").replace(PLACEHOLDER_RE, (match, key) => {
    const value = safe[key];
    if (value === undefined || value === null || value === "") return match;
    return String(value);
  });
};

export default {
  name: "TelegramSendDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    entityType: { type: String, default: null },
    entityId: { type: [Number, String], default: null },
    initialVariables: { type: Object, default: () => ({}) },
  },
  emits: ["update:modelValue", "sent", "notify"],
  data() {
    return {
      loading: false,
      sending: false,
      errorMessage: "",
      accounts: [],
      team: [],
      clients: [],
      templates: [],
      history: [],
      form: {
        accountId: null,
        // Sprint 4: cheia destinatarului ca "user:<id>" sau "contact:<id>".
        // La submit o parsăm și mapăm la userLinkId / personLinkId.
        recipientKey: null,
        templateId: null,
        text: "",
        variables: {},
      },
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit("update:modelValue", v);
      },
    },
    noBots() {
      return this.accounts.length === 0;
    },
    noRecipients() {
      return this.team.length === 0 && this.clients.length === 0;
    },
    accountOptions() {
      return this.accounts.map((a) => ({
        title: `${a.name}${a.bot_username ? ` (@${a.bot_username})` : ""}`,
        value: a.id,
      }));
    },
    // Build grouped items list. Vuetify v-select afișează entries cu
    // `props.type === 'subheader'` ca dividere non-selectabile, iar cele
    // normale ca opțiuni clicabile. Value e "user:<id>" sau "contact:<id>".
    recipientOptions() {
      const items = [];
      if (this.team.length) {
        items.push({ type: "subheader", title: this.$t("ncrm.integrations.telegram.send.groups.team") });
        this.team.forEach((r) => {
          const name = r.user_name || r.user_email || `User #${r.user_id}`;
          const tg = r.telegram_username
            ? `@${r.telegram_username}`
            : `chat ${r.telegram_chat_id}`;
          items.push({ title: `${name} (${tg})`, value: `user:${r.link_id}` });
        });
      }
      if (this.clients.length) {
        items.push({ type: "subheader", title: this.$t("ncrm.integrations.telegram.send.groups.clients") });
        this.clients.forEach((r) => {
          const name =
            [r.person_first_name, r.person_last_name].filter(Boolean).join(" ") ||
            `Persoană #${r.person_id}`;
          const company = r.person_company_name ? ` — ${r.person_company_name}` : "";
          const tg = r.telegram_username
            ? `@${r.telegram_username}`
            : `chat ${r.telegram_chat_id}`;
          items.push({ title: `${name}${company} (${tg})`, value: `contact:${r.link_id}` });
        });
      }
      return items;
    },
    templateOptions() {
      return this.templates.map((t) => ({
        title: t.name,
        value: t.id,
      }));
    },
    selectedTemplate() {
      if (!this.form.templateId) return null;
      return this.templates.find((t) => t.id === this.form.templateId) || null;
    },
    placeholders() {
      if (!this.selectedTemplate) return [];
      const declared = Array.isArray(this.selectedTemplate.variables_json)
        ? this.selectedTemplate.variables_json
            .map((v) => v?.key || v?.name)
            .filter(Boolean)
        : [];
      if (declared.length) return declared;
      return extractPlaceholders(this.selectedTemplate.body);
    },
    canSend() {
      if (this.sending) return false;
      if (!this.form.recipientKey) return false;
      if (this.form.templateId) return true;
      return !!String(this.form.text || "").trim();
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.refresh();
      else this.reset();
    },
    "form.variables": {
      deep: true,
      handler() {
        if (this.form.templateId && this.selectedTemplate) {
          this.form.text = renderBody(this.selectedTemplate.body, this.form.variables);
        }
      },
    },
  },
  methods: {
    required(value) {
      return !!value || this.$t("ncrm.integrations.telegram.send.fields.required");
    },
    formatDate(value) {
      if (!value) return "";
      try {
        return formatDisplayDateTime(value);
      } catch (_) {
        return String(value);
      }
    },
    extractError(error, fallbackKey) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        this.$t(fallbackKey)
      );
    },
    reset() {
      this.form = {
        accountId: null,
        recipientKey: null,
        templateId: null,
        text: "",
        variables: {},
      };
      this.errorMessage = "";
      this.history = [];
    },
    async refresh() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const { data } = await axios.get("/integrations/telegram/link/accounts");
        this.accounts = Array.isArray(data?.accounts) ? data.accounts : [];
        if (this.accounts.length === 0) {
          this.loading = false;
          return;
        }
        this.form.accountId = this.accounts[0].id;
        await Promise.all([this.fetchRecipients(), this.fetchTemplates(), this.fetchHistory()]);
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          "ncrm.integrations.telegram.send.errors.load"
        );
      } finally {
        this.loading = false;
      }
    },
    async onAccountChange() {
      this.form.recipientKey = null;
      await this.fetchRecipients();
    },
    async fetchRecipients() {
      if (!this.form.accountId) return;
      const { data } = await axios.get(
        `/integrations/telegram/accounts/${this.form.accountId}/recipients`
      );
      this.team = Array.isArray(data?.team) ? data.team : [];
      this.clients = Array.isArray(data?.clients) ? data.clients : [];

      // Preselectează dacă există exact un destinatar în total.
      const total = this.team.length + this.clients.length;
      if (total === 1) {
        if (this.team.length === 1) {
          this.form.recipientKey = `user:${this.team[0].link_id}`;
        } else {
          this.form.recipientKey = `contact:${this.clients[0].link_id}`;
        }
      }

      // Sprint 4 — dacă deschidem dialog-ul de pe cardul unui person link-uit,
      // preselectăm clientul respectiv automat. Operatorul aproape sigur vrea
      // să-i trimită LUI mesaj, nu unui coleg.
      if (
        this.entityType === "person" &&
        this.entityId &&
        !this.form.recipientKey
      ) {
        const match = this.clients.find((c) => Number(c.person_id) === Number(this.entityId));
        if (match) {
          this.form.recipientKey = `contact:${match.link_id}`;
        }
      }
    },
    async fetchTemplates() {
      try {
        const { data } = await axios.get("/integrations/telegram/templates");
        this.templates = Array.isArray(data?.templates) ? data.templates : [];
      } catch (_) {
        this.templates = [];
      }
    },
    async fetchHistory() {
      if (!this.entityType || !this.entityId) return;
      try {
        const { data } = await axios.get("/integrations/telegram/message-log/entity", {
          params: { entityType: this.entityType, entityId: this.entityId, limit: 5 },
        });
        this.history = Array.isArray(data?.messages) ? data.messages : [];
      } catch (_) {
        this.history = [];
      }
    },
    onTemplateChange(newId) {
      this.errorMessage = "";
      if (!newId) {
        this.form.text = "";
        this.form.variables = {};
        return;
      }
      const tpl = this.templates.find((t) => t.id === newId);
      if (!tpl) return;
      // Seed variables: first try declared defaults, then initialVariables prop.
      const declared = Array.isArray(tpl.variables_json) ? tpl.variables_json : [];
      const seeded = {};
      declared.forEach((d) => {
        const key = d?.key || d?.name;
        if (!key) return;
        seeded[key] = this.initialVariables[key] ?? d?.default ?? "";
      });
      // Fallback: any placeholder in body that the operator might want to fill.
      extractPlaceholders(tpl.body).forEach((key) => {
        if (!(key in seeded)) {
          seeded[key] = this.initialVariables[key] ?? "";
        }
      });
      this.form.variables = seeded;
      this.form.text = renderBody(tpl.body, seeded);
    },
    close() {
      this.dialog = false;
    },
    async submit() {
      if (!this.canSend) return;
      this.sending = true;
      this.errorMessage = "";
      try {
        // Parse recipientKey: "user:<id>" sau "contact:<id>". Trimitem doar
        // un singur ID — gateway-ul respinge ambele (consistent cu service).
        const [kind, rawId] = String(this.form.recipientKey || "").split(":");
        const linkId = Number(rawId);
        if (!linkId || (kind !== "user" && kind !== "contact")) {
          this.errorMessage = this.$t("ncrm.integrations.telegram.send.errors.send");
          this.sending = false;
          return;
        }
        const payload = {
          accountId: this.form.accountId,
          relatedEntityType: this.entityType || null,
          relatedEntityId: this.entityId || null,
        };
        if (kind === "user") {
          payload.userLinkId = linkId;
        } else {
          payload.personLinkId = linkId;
        }
        if (this.form.templateId) {
          payload.templateId = this.form.templateId;
          payload.variables = this.form.variables;
        } else {
          payload.text = this.form.text;
        }
        const { data } = await axios.post("/integrations/telegram/send", payload, {
          validateStatus: () => true,
        });
        if (data?.ok) {
          this.$emit("sent", data);
          this.$emit("notify", {
            message: this.$t("ncrm.integrations.telegram.send.snackbar.sent"),
            color: "success",
          });
          this.dialog = false;
        } else {
          this.errorMessage =
            data?.error ||
            data?.message ||
            this.$t("ncrm.integrations.telegram.send.errors.send");
        }
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          "ncrm.integrations.telegram.send.errors.send"
        );
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.telegram-send-history-row {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}
.telegram-send-history-row:last-child {
  border-bottom: none;
}
.telegram-send-history-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.85rem;
}
</style>
